import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  UserPlus, Trash2, X, ScanFace, Mic, MicOff,
  Fingerprint, CheckCircle2, Loader2, SkipForward, RefreshCcw,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useCamera } from "@/hooks/useCamera";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";

const TONE_OPTIONS = [
  { value: "informal", label: "Informal / Warm" },
  { value: "neutral", label: "Neutral / Courteous" },
  { value: "formal", label: "Formal / Diplomatic" },
];

function PersonCard({ person, onDelete, onTrain }) {
  const needsTraining = !person.has_face || !person.has_voice;
  return (
    <motion.div
      data-testid={`person-card-${person.id}`}
      className="bg-[#0B0E14] border border-[#1A202C] rounded-xl p-4 flex flex-col gap-2"
      whileHover={{ y: -2 }}
    >
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-cyan-400/10 border border-cyan-400/30">
          <Fingerprint className="w-4 h-4 text-cyan-300" />
        </div>
        <button
          data-testid={`delete-person-${person.id}`}
          onClick={() => onDelete(person.id)}
          className="text-slate-500 hover:text-rose-400 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div>
        <p className="font-display font-semibold text-sm text-white truncate">{person.name}</p>
        {person.relationship && <p className="text-[10px] font-mono-data text-slate-500 truncate mt-0.5">{person.relationship}</p>}
      </div>
      {person.notes && <p className="text-[11px] text-slate-400 line-clamp-2">{person.notes}</p>}
      <div className="flex items-center gap-2 mt-1 pt-2 border-t border-white/5">
        <span
          className={`text-[9px] font-mono-data uppercase px-2 py-0.5 rounded-full ${person.has_face ? "bg-emerald-400/15 text-emerald-300" : "bg-white/5 text-slate-500"}`}
          data-testid={`person-face-status-${person.id}`}
        >
          {person.has_face ? "Face trained" : "No face"}
        </span>
        <span
          className={`text-[9px] font-mono-data uppercase px-2 py-0.5 rounded-full ${person.has_voice ? "bg-emerald-400/15 text-emerald-300" : "bg-white/5 text-slate-500"}`}
          data-testid={`person-voice-status-${person.id}`}
        >
          {person.has_voice ? "Voice trained" : "No voice"}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        {person.interaction_count > 0 && (
          <p className="text-[9px] font-mono-data text-slate-600">Recognized {person.interaction_count}x</p>
        )}
        {needsTraining && (
          <button
            data-testid={`train-person-${person.id}`}
            onClick={() => onTrain(person)}
            title="Add face and/or voice sample so AVA recognizes this person live"
            className="ml-auto flex items-center gap-1 text-[9px] font-mono-data uppercase tracking-widest text-cyan-300 hover:bg-cyan-400/15 border border-cyan-400/30 rounded-full px-2 py-0.5 transition-colors"
          >
            <ScanFace className="w-2.5 h-2.5" /> Train
          </button>
        )}
      </div>
    </motion.div>
  );
}

function EnrollModal({ onClose, onDone, existingPerson = null }) {
  // If we're retraining an already-created person we skip the form step and go
  // straight to face capture - Doc doesn't need to re-enter name/notes just to
  // add a face or voice sample later.
  const [step, setStep] = useState(existingPerson ? "face" : "form"); // form | face | voice
  const [name, setName] = useState(existingPerson?.name || "");
  const [relationship, setRelationship] = useState(existingPerson?.relationship || "");
  const [tone, setTone] = useState(existingPerson?.tone || "informal");
  const [notes, setNotes] = useState(existingPerson?.notes || "");
  const [saving, setSaving] = useState(false);
  const [personId, setPersonId] = useState(existingPerson?.id || null);
  const { videoRef, active, facing, start, stop, capture, flip } = useCamera();
  const { recording, error: micError, start: startRec, stop: stopRec } = useAudioRecorder();
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    // When we open in retraining mode we need to kick the camera on immediately
    // since the form step (which normally does that) is skipped.
    if (existingPerson) {
      start().catch(() => {});
    }
  }, [existingPerson, start]);

  const handleCreatePerson = async () => {
    if (!name.trim()) {
      toast.error("Give this person a name first.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/people", { name: name.trim(), relationship, tone, notes });
      setPersonId(res.data.id);
      setStep("face");
      await start();
    } catch (e) {
      toast.error("Could not create person.");
    } finally {
      setSaving(false);
    }
  };

  const handleCaptureFace = async () => {
    const image = capture();
    if (!image) {
      toast.error("Camera isn't ready yet.");
      return;
    }
    setCapturing(true);
    try {
      await api.post(`/people/${personId}/enroll-face`, { image });
      toast.success("Face trained.");
      stop();
      setStep("voice");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No face detected - try again with better lighting.");
    } finally {
      setCapturing(false);
    }
  };

  const handleVoiceToggle = async () => {
    if (recording) {
      const audio = await stopRec();
      if (!audio) return;
      setCapturing(true);
      try {
        await api.post(`/people/${personId}/enroll-voice`, { audio });
        toast.success("Voice trained.");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not extract a voice print - try again.");
      } finally {
        setCapturing(false);
      }
    } else {
      startRec();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-panel rounded-2xl p-6 w-full max-w-md"
        data-testid="enroll-person-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-white text-base">{existingPerson ? `Train ${existingPerson.name}` : "Enroll New Person"}</h3>
          <button data-testid="close-enroll-modal" onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === "form" && (
          <>
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              data-testid="person-name-input"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-cyan-400/40 mb-3"
            />
            <input
              value={relationship} onChange={(e) => setRelationship(e.target.value)}
              placeholder="Relationship (optional)"
              data-testid="person-relationship-input"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-cyan-400/40 mb-3"
            />
            <select
              value={tone} onChange={(e) => setTone(e.target.value)}
              data-testid="person-tone-select"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-cyan-400/40 mb-3"
            >
              {TONE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes AVA should remember about them (optional)"
              data-testid="person-notes-input"
              rows={3}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-cyan-400/40 mb-5 resize-none"
            />
            <button
              onClick={handleCreatePerson}
              disabled={saving}
              data-testid="create-person-continue-button"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border border-cyan-400/30 text-xs font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
            >
              {saving ? "Creating..." : "Continue to Face Capture"}
            </button>
          </>
        )}

        {step === "face" && (
          <>
            <p className="text-[11px] text-slate-500 mb-3">Center {name}'s face in frame, well-lit, looking at the camera.</p>
            <div className="relative aspect-video bg-black/60 rounded-xl border border-white/10 overflow-hidden mb-3" data-testid="face-capture-preview">
              <video ref={videoRef} className={`w-full h-full object-cover ${active ? "" : "hidden"}`} muted playsInline />
              {active && (
                <button
                  data-testid="enroll-flip-camera"
                  onClick={flip}
                  title={`Switch to ${facing === "environment" ? "front" : "rear"} camera`}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center bg-black/70 hover:bg-black/85 border border-white/15 text-slate-200 transition-colors"
                >
                  <RefreshCcw className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              onClick={handleCaptureFace}
              disabled={capturing || !active}
              data-testid="capture-face-button"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border border-cyan-400/30 text-xs font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50 mb-2"
            >
              {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanFace className="w-3.5 h-3.5" />}
              {capturing ? "Training..." : "Capture Face"}
            </button>
            <button
              onClick={() => { stop(); setStep("voice"); }}
              data-testid="skip-face-button"
              className="w-full flex items-center justify-center gap-2 py-2 text-slate-500 hover:text-slate-300 text-[11px] font-mono-data uppercase tracking-wider transition-colors"
            >
              <SkipForward className="w-3 h-3" /> Skip
            </button>
          </>
        )}

        {step === "voice" && (
          <>
            <p className="text-[11px] text-slate-500 mb-3">Optional: record 3-5 seconds of {name} speaking, so AVA recognizes their voice too.</p>
            {micError && <p className="text-[11px] text-rose-400 mb-2" data-testid="mic-error">{micError}</p>}
            <button
              onClick={handleVoiceToggle}
              disabled={capturing}
              data-testid="record-voice-button"
              className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border text-xs font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50 mb-2 ${recording ? "bg-rose-400/15 text-rose-300 border-rose-400/30" : "bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border-cyan-400/30"}`}
            >
              {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : recording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
              {capturing ? "Training..." : recording ? "Stop Recording" : "Record Voice Sample"}
            </button>
            <button
              onClick={() => { onDone(); onClose(); }}
              data-testid="finish-enroll-button"
              className="w-full flex items-center justify-center gap-2 py-2 text-slate-400 hover:text-white text-[11px] font-mono-data uppercase tracking-wider transition-colors"
            >
              <CheckCircle2 className="w-3 h-3" /> Done
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function RecognizeModal({ onClose }) {
  const { videoRef, active, facing, start, capture, flip } = useCamera();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { start(); }, [start]);

  const handleRecognize = async () => {
    const image = capture();
    if (!image) return;
    setScanning(true);
    try {
      const res = await api.post("/people/recognize", { image });
      setResult(res.data);
    } catch (e) {
      toast.error("Recognition failed.");
    } finally {
      setScanning(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-panel rounded-2xl p-6 w-full max-w-md"
        data-testid="recognize-person-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-white text-base">Who&apos;s Here?</h3>
          <button data-testid="close-recognize-modal" onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="relative aspect-video bg-black/60 rounded-xl border border-white/10 overflow-hidden mb-3">
          <video ref={videoRef} className={`w-full h-full object-cover ${active ? "" : "hidden"}`} muted playsInline />
          {active && (
            <button
              data-testid="recognize-flip-camera"
              onClick={flip}
              title={`Switch to ${facing === "environment" ? "front" : "rear"} camera`}
              className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center bg-black/70 hover:bg-black/85 border border-white/15 text-slate-200 transition-colors"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={handleRecognize}
          disabled={scanning}
          data-testid="recognize-scan-button"
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border border-cyan-400/30 text-xs font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
        >
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Fingerprint className="w-3.5 h-3.5" />}
          {scanning ? "Scanning..." : "Recognize"}
        </button>
        {result && (
          <div className="mt-4 text-center" data-testid="recognize-result">
            {result.matched ? (
              <p className="text-emerald-300 text-sm font-display font-semibold">
                Recognized: {result.person.name} ({Math.round((result.confidence || 0) * 100)}% match via {result.method})
              </p>
            ) : (
              <p className="text-slate-400 text-sm">No match found in AVA's people memory.</p>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function PeopleMemory() {
  const [people, setPeople] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [training, setTraining] = useState(null); // existing person being retrained
  const [recognizing, setRecognizing] = useState(false);

  const fetchPeople = () => api.get("/people").then((res) => setPeople(res.data)).catch(() => {});

  useEffect(() => {
    fetchPeople();
  }, []);

  const handleDelete = async (id) => {
    await api.delete(`/people/${id}`);
    setPeople((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <div className="col-span-1 md:col-span-10 h-full glass-panel rounded-2xl p-4 sm:p-6 overflow-y-auto" data-testid="people-memory-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-white text-lg">People Memory</h2>
          <p className="text-[11px] text-slate-500 mt-1">{people.length} people enrolled - AVA auto-adapts tone when it recognizes them</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="recognize-who-button"
            onClick={() => setRecognizing(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-[11px] font-mono-data uppercase tracking-wider transition-colors"
          >
            <Fingerprint className="w-3.5 h-3.5" /> Who's Here?
          </button>
          <button
            data-testid="enroll-person-button"
            onClick={() => setEnrolling(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/25 text-cyan-300 text-[11px] font-mono-data uppercase tracking-wider transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" /> Enroll Person
          </button>
        </div>
      </div>

      {people.length === 0 ? (
        <div className="flex items-center justify-center text-center text-slate-500 text-xs p-12 border border-dashed border-white/10 rounded-xl" data-testid="no-people-enrolled">
          No one enrolled yet. Enroll Nasser, Ahmad, Deborah, or anyone else AVA should remember.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {people.map((p) => <PersonCard key={p.id} person={p} onDelete={handleDelete} onTrain={setTraining} />)}
        </div>
      )}

      {enrolling && <EnrollModal onClose={() => setEnrolling(false)} onDone={fetchPeople} />}
      {training && <EnrollModal existingPerson={training} onClose={() => setTraining(null)} onDone={fetchPeople} />}
      {recognizing && <RecognizeModal onClose={() => setRecognizing(false)} />}
    </div>
  );
}
